import React from 'react';
import { Box, IconButton, Popover, Slider, Stack, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { OptionsType, useEventContext } from 'providers/EventProvider';

const TAIL_SLIDER_MAX = 610;
const TAIL_FROM_BEGINNING = 10000;

const formatTail = (tail: number) => {
  if (tail >= TAIL_FROM_BEGINNING) return 'from beginning';
  if (tail < 60) return `${tail} sec`;
  const minutes = Math.floor(tail / 60);
  const seconds = tail % 60;
  if (seconds === 0) return minutes === 1 ? '1 min' : `${minutes} min`;
  return `${minutes} min ${seconds} sec`;
};

const tailToSlider = (tail: number) => (tail >= TAIL_FROM_BEGINNING ? TAIL_SLIDER_MAX : Math.min(tail, 600));
const sliderToTail = (value: number) => (value >= TAIL_SLIDER_MAX ? TAIL_FROM_BEGINNING : value);

const SelectPreference = () => {
  const { options, updateOptions } = useEventContext();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const onChangeOption = (key: keyof Pick<OptionsType, 'tail' | 'routeOpacity' | 'routeWeight'>) => (_: Event, value: number | number[]) => {
    const next = Array.isArray(value) ? value[0] : value;
    updateOptions({
      ...options,
      [key]: key === 'tail' ? sliderToTail(next) : next
    });
  };

  return (
    <>
      <IconButton
        aria-label="Display preferences"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="small"
      >
        <TuneIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 260 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" gutterBottom>
                Tail: {formatTail(options.tail)}
              </Typography>
              <Slider
                size="small"
                min={0}
                max={TAIL_SLIDER_MAX}
                step={30}
                value={tailToSlider(options.tail)}
                onChange={onChangeOption('tail')}
                valueLabelDisplay="off"
              />
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom>
                Opacity: {Math.round(options.routeOpacity * 100)}%
              </Typography>
              <Slider
                size="small"
                min={0.1}
                max={1}
                step={0.05}
                value={options.routeOpacity}
                onChange={onChangeOption('routeOpacity')}
                valueLabelDisplay="off"
              />
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom>
                Weight: {options.routeWeight}
              </Typography>
              <Slider
                size="small"
                min={1}
                max={10}
                step={1}
                value={options.routeWeight}
                onChange={onChangeOption('routeWeight')}
                valueLabelDisplay="off"
              />
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default SelectPreference;
